/* Optional cart-checkout CTA. Never rewrites a Shopify accelerated button. */
(() => {
  'use strict';
  if (customElements.get('aapka-buy-now')) return;

  class AapkaBuyNow extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.observer?.disconnect();
      this.request?.abort();
      this.restoreAddButton();
    }

    initialize() {
      this.events?.abort();
      this.observer?.disconnect();
      this.wrapper = this.closest('[data-aapka-buy-buttons]');
      this.form = document.getElementById(this.dataset.formId);
      this.button = this.querySelector('[data-aapka-buy-now-button]');
      this.label = this.querySelector('[data-aapka-buy-now-label]');
      this.loading = this.querySelector('[data-aapka-buy-now-loading]');
      this.productForm = this.closest('product-form');
      this.addButton = this.productForm?.querySelector('[name="add"]');
      if (!this.wrapper || !this.form || !this.button || !this.addButton) return;
      this.events = new AbortController();
      this.button.addEventListener('click', () => this.checkout(), { signal: this.events.signal });
      this.observer = new MutationObserver(() => this.scheduleSync());
      this.observer.observe(this.wrapper, { childList: true, subtree: true });
      this.observer.observe(this.addButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
      this.form.addEventListener('change', () => this.scheduleSync(), { signal: this.events.signal });
      window.addEventListener('pageshow', (event) => {
        // Returning from checkout through the back/forward cache must not leave
        // this button in its previous successful-navigation loading state.
        if (!event.persisted) return;
        this.busy = false;
        this.restoreAddButton();
        this.button.removeAttribute('aria-busy');
        this.label.hidden = false;
        this.loading.hidden = true;
        this.sync();
      }, { signal: this.events.signal });
      this.sync();
    }

    scheduleSync() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    }

    sync() {
      if (!this.button || !this.isConnected) return;
      const state = this.wrapper.querySelector('[data-aapka-purchase-state]');
      const variantId = this.form.querySelector('[name="id"]')?.value;
      const stateMatches = Boolean(state && state.dataset.variantId === variantId);
      const unavailable = this.addButton.disabled || this.addButton.getAttribute('aria-disabled') === 'true';
      this.button.disabled = Boolean(this.busy || unavailable || !stateMatches);
      if (this.busy) return;
      const label = stateMatches
        ? `Buy Now — ${state.dataset.formattedPrice}`
        : 'Buy Now';
      if (this.label.textContent.trim() !== label) this.label.textContent = label;
    }

    showError(message) {
      if (typeof this.productForm?.handleErrorMessage === 'function') {
        this.productForm.handleErrorMessage(message);
        return;
      }
      const wrapper = this.wrapper.querySelector('.product-form__error-message-wrapper');
      const text = wrapper?.querySelector('.product-form__error-message');
      if (text) text.textContent = message;
      if (wrapper) wrapper.hidden = false;
    }

    restoreAddButton() {
      if (!this.lockedAddButton) return;
      if (this.previousAria === null) this.lockedAddButton.removeAttribute('aria-disabled');
      else this.lockedAddButton.setAttribute('aria-disabled', this.previousAria);
      this.lockedAddButton = null;
    }

    async checkout() {
      this.sync();
      if (this.busy || this.button.disabled) return;
      // Bundler controls bundle composition itself. Do not bypass that flow.
      if (document.querySelector('.bundler-widget-wrapper')) {
        this.showError('Please use the bundle purchase controls or Add to Cart for this offer.');
        return;
      }
      if (!this.form.reportValidity()) return;
      const data = new FormData(this.form);
      if (!data.get('id')) return;
      data.delete('sections');
      data.delete('sections_url');
      data.delete('return_to');

      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.label.hidden = true;
      this.loading.hidden = false;
      this.lockedAddButton = this.addButton;
      this.previousAria = this.addButton.getAttribute('aria-disabled');
      this.addButton.setAttribute('aria-disabled', 'true');
      this.productForm?.handleErrorMessage?.();
      this.request = new AbortController();
      const timer = setTimeout(() => this.request?.abort(), 20000);
      let navigating = false;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const addUrl = window.routes?.cart_add_url || `${root}cart/add`;
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data,
          signal: this.request.signal,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const message = typeof result.description === 'string'
            ? result.description
            : 'This item could not be added. Please check your selection and try again.';
          this.showError(message);
          return;
        }
        if (!this.isConnected) return;
        // Standard Shopify cart checkout. Existing items are not cleared.
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = this.dataset.cartUrl || window.routes?.cart_url || `${root}cart`;
        checkoutForm.hidden = true;
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        checkoutForm.appendChild(checkoutInput);
        document.body.appendChild(checkoutForm);
        HTMLFormElement.prototype.submit.call(checkoutForm);
        navigating = true;
      } catch (_) {
        if (this.isConnected) {
          this.showError('We could not confirm the checkout request. Check your cart before retrying to avoid adding the item twice.');
        }
      } finally {
        clearTimeout(timer);
        this.request = null;
        this.restoreAddButton();
        if (!navigating && this.isConnected) {
          this.busy = false;
          this.button.removeAttribute('aria-busy');
          this.label.hidden = false;
          this.loading.hidden = true;
          this.sync();
        }
      }
    }
  }

  customElements.define('aapka-buy-now', AapkaBuyNow);
})();
